import { useRef, useState, useEffect, useMemo } from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { UnfoldMore } from "@remo-co/ui-core/src/icons/UnfoldMore";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import {
  selectCurrentTheaterId,
  selectTheaterSpaces,
} from "modules/theater/selectors";
import { selectUserForceMoveData } from "modules/userForceMove/redux/selectors";
import { selectUserId } from "modules/auth/redux/selectors";
import { usePrevious } from "helpers/reactHooksUtils";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import { useFloorName } from "modules/floors/hooks/useFloorName";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useStyles } from "./styles";
import { FloorSwitchPopover } from "./FloorSwitchPopover";

const FloorSwitch = () => {
  const styles = useStyles();
  const { t } = useI18n(["common", "event"]);
  const [showMenu, setShowMenu] = useState(false);
  const floorsRef = useRef(null);
  const spaces = useSelector(selectTheaterSpaces);
  const currentSpaceId = useSelector(selectCurrentSpaceId);
  const userForceMoveData = useSelector(selectUserForceMoveData);
  const currentUserId = useSelector(selectUserId);
  const currentTheaterId = useSelector(selectCurrentTheaterId);
  const floorName = useFloorName(currentTheaterId, currentSpaceId);
  const currentUserIsBeingMoved =
    userForceMoveData && userForceMoveData.userId === currentUserId;
  const prevCurrentUserIsBeingMoved = usePrevious(currentUserIsBeingMoved);

  useEffect(() => {
    if (currentUserIsBeingMoved && !prevCurrentUserIsBeingMoved) {
      setShowMenu(false);
    }
  }, [currentUserIsBeingMoved, prevCurrentUserIsBeingMoved]);

  const currentFloorNumber = useMemo(
    () =>
      currentSpaceId
        ? spaces.findIndex((space) => space === currentSpaceId) + 1
        : null,
    [currentSpaceId, spaces],
  );

  if (spaces.length <= 1) {
    return null;
  }

  const toggleMenu = () => {
    setShowMenu((isVisible) => !isVisible);
  };

  const disableFloorSwitchButton = currentUserIsBeingMoved;

  return (
    <>
      <Button
        ref={floorsRef}
        onClick={toggleMenu}
        variant="text"
        className={styles.floors}
        disabled={disableFloorSwitchButton}
        contentClassName={styles.btnText}
        data-testid="floor-switch-button"
      >
        <Typography variant="h4">
          {/* eslint-disable-next-line no-nested-ternary */}
          {currentFloorNumber && currentFloorNumber !== -1
            ? currentSpaceId && floorName
              ? floorName
              : t("event:top.bar.floor", {
                  floor: currentFloorNumber,
                })
            : t("event:top.bar.floors")}
        </Typography>
        <UnfoldMore className={styles.unfoldIcon} />
      </Button>
      {showMenu && (
        <FloorSwitchPopover
          showMenu={showMenu}
          setShowMenu={setShowMenu}
          anchorRef={floorsRef}
        />
      )}
    </>
  );
};

export default FloorSwitch;
